import { useQuery } from '@tanstack/react-query';
import { staffAPI } from '../services/api';

// Dashboard statistics - disabled as metrics are no longer displayed
// Keeping hook for potential future use with proper permission checks
export const useDashboardStats = () => {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      try {
        const response = await staffAPI.getDashboardStats();
        return response.data || {};
      } catch (error) {
        // Silently handle 403 errors (insufficient permissions) - dashboard stats restricted to HR/admin
        if (error.response?.status === 403) {
          console.log('Dashboard stats access denied - restricted to HR/admin users');
          return {}; // Return empty object instead of throwing
        }
        throw error; // Re-throw other errors
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
    retry: (failureCount, error) => {
      // Don't retry 403 errors
      if (error?.response?.status === 403) return false;
      return failureCount < 2;
    },
    enabled: false, // Disabled since metrics are not displayed
  });
};

// Headcount movements (joins/separations) for the Avg Monthly Change drill-down modal.
// Lazy: only fetches when `enabled` is true (i.e. the modal is open).
export const useHeadcountMovements = (enabled = false) => {
  return useQuery({
    queryKey: ['dashboard', 'headcount-movements'],
    queryFn: async () => {
      const response = await staffAPI.getHeadcountMovements();
      return response.data || null;
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes — headcount moves slowly
    retry: (failureCount, error) => {
      if (error?.response?.status === 403) return false;
      return failureCount < 2;
    },
  });
};

// Recent leave applications for dashboard
export const useRecentLeaveApplications = (limit = 5) => {
  return useQuery({
    queryKey: ['dashboard', 'leave-applications', limit],
    queryFn: async () => {
      const response = await staffAPI.getLeaveApplications({ 
        page_size: limit,
        ordering: '-created_date'
      });
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Pending document approvals for dashboard
export const usePendingDocumentApprovals = () => {
  return useQuery({
    queryKey: ['dashboard', 'document-approvals', 'pending'],
    queryFn: async () => {
      const response = await staffAPI.getPendingDocumentApprovals();
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      // Don't retry if forbidden (user might not have access)
      if (error?.response?.status === 403) return false;
      return failureCount < 2;
    },
  });
};

// Employee count and demographics
export const useEmployeeStats = () => {
  return useQuery({
    queryKey: ['dashboard', 'employee-stats'],
    queryFn: async () => {
      const response = await staffAPI.getStaffStatistics();
      const data = response.data || {};
      
      // Transform backend field names to frontend expected field names
      return {
        total: data.total_staff || 0,
        newHires: data.new_hires_this_year || 0,
        probation: data.probation_staff || 0,
        avgTenure: data.avg_tenure_years || 0,
        departments: data.unique_departments || 0,
        systemAccounts: data.system_accounts || 0,
        totalIncludingInactive: data.total_including_inactive || 0,
        // Keep original data for debugging
        ...data
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Attendance summary for dashboard
export const useAttendanceDashboard = () => {
  return useQuery({
    queryKey: ['dashboard', 'attendance-summary'],
    queryFn: async () => {
      const response = await staffAPI.getAttendanceSummary();
      return response.data || {};
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
